import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useField } from 'react-form';

import useMemoDeep from '../../../utils/useMemoDeep';

import FormGroup from './FormGroup';
import Select from './Select';

const defaultChipStyles = {};

const SelectGroup = ({
  FormGroupProps = undefined,
  'aria-label': ariaLabel = null,
  async = undefined,
  chipStyles = defaultChipStyles,
  className = null,
  clearAriaLabel = null,
  disabled = false,
  field,
  filterOption = undefined,
  helperText = null,
  hideDropdownIcon = false,
  hideSelectedOptions = false,
  hoverOptions = false,
  isClearable = undefined,
  isMulti = false,
  isRequiredTitleLabel = false,
  loadOptions = undefined,
  onMenuClose = null,
  onMenuOpen = null,
  onSelected = null,
  options,
  placeholder = null,
  startIcon = null,
  title = undefined,
  validate = null,
  wrap = false,
  ...selectProps
}) => {
  const { value, setValue, meta: { error } } = useField(field, { validate });
  const onChange = useCallback((params) => {
    let newValue;
    if (isMulti) {
      // Multi params: [{ label: "Label 1", value: "Value 1" }]
      newValue = (params || []).map(({ value: v }) => v);
    } else {
      // Single params: { label: "Label", value: "Value" }
      newValue = (params || {}).value;
    }
    if (typeof newValue === 'undefined') {
      newValue = null;
    }
    if (typeof onSelected === 'function') {
      onSelected(newValue);
    }
    setValue(newValue);
  }, [isMulti, setValue, onSelected]);

  // Compare JSX-label options by value so React element owner references stay out of deep comparisons.
  const optionDependencies = options.map(option => (typeof option.label === 'string' ? option : option.value));
  const selectOptions = useMemoDeep(() => options.map(({ value: v, ...option }) => ({
    ...option,
    value: v,
  })), [optionDependencies]); // eslint-disable-line react-hooks/exhaustive-deps -- optionDependencies intentionally excludes JSX labels.

  // Format validation error
  let errorMessage = error;
  if (errorMessage && (title || placeholder)) {
    errorMessage = `${title || placeholder} ${errorMessage.toLowerCase()}`;
  }

  return (
    <FormGroup {...FormGroupProps} error={errorMessage}>
      <Select
        aria-label={ariaLabel}
        async={async}
        chipStyles={chipStyles}
        className={className}
        clearAriaLabel={clearAriaLabel}
        disabled={disabled}
        helperText={helperText}
        hideDropdownIcon={hideDropdownIcon}
        hideSelectedOptions={hideSelectedOptions}
        isClearable={typeof isClearable !== 'undefined' ? isClearable : !validate}
        isRequiredTitleLabel={isRequiredTitleLabel}
        isMulti={isMulti}
        inputClassName={className}
        loadOptions={loadOptions}
        name={field}
        onChange={onChange}
        onMenuClose={onMenuClose}
        onMenuOpen={onMenuOpen}
        options={selectOptions}
        placeholder={placeholder}
        hoverOptions={hoverOptions}
        startIcon={startIcon}
        title={title}
        value={value}
        wrap={wrap}
        filterOption={filterOption}
        {...selectProps}
      />
    </FormGroup>
  );
};

SelectGroup.propTypes = {
  'aria-label': PropTypes.string,
  async: PropTypes.bool,
  chipStyles: PropTypes.object,
  className: PropTypes.string,
  clearAriaLabel: PropTypes.string,
  disabled: PropTypes.bool,
  field: PropTypes.string.isRequired,
  filterOption: PropTypes.func,
  /** Props applied to the internal FormGroup component. */
  FormGroupProps: PropTypes.object,
  helperText: PropTypes.string,
  hideDropdownIcon: PropTypes.bool,
  hideSelectedOptions: PropTypes.bool,
  hoverOptions: PropTypes.bool,
  isClearable: PropTypes.bool,
  isMulti: PropTypes.bool,
  isRequiredTitleLabel: PropTypes.bool,
  loadOptions: PropTypes.func,
  onMenuClose: PropTypes.func,
  onMenuOpen: PropTypes.func,
  onSelected: PropTypes.func,
  options: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.node,
    value: PropTypes.oneOfType([PropTypes.bool, PropTypes.number, PropTypes.string]),
  })).isRequired,
  placeholder: PropTypes.string,
  startIcon: PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.string]),
  title: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.string,
  ]),
  validate: PropTypes.func,
  wrap: PropTypes.bool,
};

export default SelectGroup;
